(ns storrito.connect3.two-factor-panel
  (:require [storrito.ui.button :as button]
            [storrito.connect3.common :as common]
            [clojure.string :as str]
            ))

(defn get-hint
  "Provides the customer with instructions where to find the two-factor-code that
   Instagram has sent. The numbers are for the legacy-login while the strings are
   for bloks."
  [two-factor-challenge]
  (case two-factor-challenge
    (1 "sms") "Instagram has sent you a code via SMS"
    (2 "backup_codes") "Please enter an unused backup code for your Instagram account"
    (3 "totp") "Enter a code from your authenticator app (like Google Authenticator)"

    ;; (4) notification is handled by a different screen

    (;; TODO: 5 is probably email?
     "email") "Instagram has sent you a code via email"
    (6
      "whatsapp" ;; TODO: is this correct?
      )
    "Instagram has sent you a code via WhatsApp"

    ;; default:
    (list "Instagram has sent you a "
          [:b "code"] " by" [:br]
          [:b "SMS, email "] "or "
          [:b " authenticator app (Google Authenticator)"])))

(defn input-validation
  [digits-count event]
  ;; You could also register a corresponding data-driven
  ;; event-handler into the global one, but using inline
  ;; code for plain input validation feels more pragmatic:
  (set! event.target.value
        (-> event.target.value
            (subs 0 digits-count)
            (str/replace #"[^0-9]" ""))))

(defn code-submission
  "The action that sends the entered code, or nil while the code is not
   valid yet. Enter in an empty field used to send anyway, which reached
   PHP as an empty twofacode."
  [state]
  (when (:twofacode-valid state)
    [:connect3/send! (select-keys state [:twofacode])]))

(defn- on
  [state digits-count]
  {:change [[:store/assoc-in [:connect3 :twofacode] :event/target.value]
            [:connect3/validate-twofacode]]
   :input (partial input-validation
                   digits-count)
   :keyup (when-let [send (code-submission state)]
            [[:js/on-enter] send])
   })

(defn submit-button
  [state]
  (button/button-primary-lg
    (merge
      {:class "mt-4"
       :on {:click (when-let [send (code-submission state)]
                     [send])}})
    [:p "Submit"]))

(defn panel
  [state]
  (let [username (:username state)
        use-back-up (= "backup_codes"
                       (:2fa-verification-method state))
        digits-count (if use-back-up 8 6)]
    [:div.flex.items-center.justify-center
     [:div.bg-white.shadow-xl.rounded-2xl.px-8.pt-10.pb-10.max-w-xl.w-full.mx-auto.flex.flex-col.items-center
      (common/instagram-logo)
      [:h2.text-2xl.font-bold.text-gray-900.mt-4.mb-2.text-center.w-full
       "Two-factor authentication (2FA) enabled for"]
      [:p.text-lg.text-gray-700.mb-6.text-center.w-full
       (if username
         [:span [:b username]]
         nil)]
      [:div.text-center.mb-5.text-base.text-gray-800
       (get-hint (:twoFactorChallenge state))]
      [:div.flex.flex-col.justify-center.items-center.w-full
       ;; Wrapper div for password-manager compatibility, see comment in
       ;; `storrito.connect3.login-panel/panel`.
       [:div
        [:input#twofacode.px-5.py-3.placeholder-gray-500.border-gray-400.rounded-md.border-2.text-center.text-2xl.tracking-widest.bg-gray-50.focus:outline-none.focus:ring-2.focus:ring-blue-500.focus:border-blue-500.transition-all.duration-200.mb-4
         {:style {:width "200px"}
          :name "twofacode"
          :type "text"
          :required true
          :placeholder (if use-back-up
                         "- - - - - - - -"
                         "- - - - - -")
          :on (on state digits-count)
          :autoFocus true}]]
       (common/error-message state)
       (button/button-primary-lg
         {:class "w-full mt-4"
          :on {:click (when-let [send (code-submission state)]
                        [send])}}
         "Submit")]]]))

(defn generic-panel
  [state]
  (let [username (:username state)
        step-hint (:step-hint state)
        digits-count 6]
    [:div.flex.items-center.justify-center
     [:div.bg-white.shadow-xl.rounded-2xl.px-8.pt-6.pb-6.max-w-md.w-full.mx-auto.flex.flex-col.items-center
      (common/instagram-logo)

      ;; Instagram's generic code screen does not tell us where the code
      ;; went (`step-hint` is empty in every session of Sept 2026), and it
      ;; is usually the phone: SMS and WhatsApp were the working code
      ;; sources of the 2FA picker (13 of 18), email was none. The panel
      ;; used to be headed "Check your email" with an envelope and email-only
      ;; tips - 54 of 153 users (35 %) never typed anything and the
      ;; 10-minute session ran out on them.
      [:div.flex.items-center.justify-center.bg-blue-100.text-blue-600.rounded-full.mt-4.mb-3
       {:style {:width "56px" :height "56px"}}
       [:i.fas.fa-shield-alt.fa-lg]]

      [:div
       [:h2.text-2xl.font-bold.text-gray-900.mb-1.text-center.w-full
        "Enter the code from Instagram"]

       (if-let [contact (:contact step-hint)]
               [:p.text-base.text-gray-700.mb-4.text-center.w-full contact]
               [:p.text-base.text-gray-700.mb-4.text-center.w-full
                "Instagram just sent a 6-digit code to the phone number or email linked to "
                (if username
                  [:b username]
                  "your account")
                " — check your SMS and WhatsApp messages first, then your inbox."])]

      [:div.w-full.bg-blue-50.border.border-blue-200.rounded-lg.p-3.mb-4
       [:p.text-sm.font-semibold.text-gray-800.mb-2.flex.items-center
        [:i.fas.fa-info-circle.text-blue-600.mr-2]
        "No code yet?"]
       [:ul.text-sm.text-gray-700.list-disc.pl-5.space-y-1
        [:li "Give it up to a minute — Instagram can be slow."]
        [:li "Phone: look in SMS and WhatsApp, also from unknown senders."]
        [:li "Email: check spam and promotions for a sender from " [:b "instagram.com"] "."]
        [:li "Keep this page open — the code stays valid here for 10 minutes."]]]

      [:div.flex.flex-col.justify-center.items-center.w-full
       ;; Wrapper div for password-manager compatibility, see comment in
       ;; `storrito.connect3.login-panel/panel`.
       [:div
        [:input#twofacode.px-5.py-3.placeholder-gray-500.border-gray-400.rounded-md.border-2.text-center.text-2xl.tracking-widest.bg-gray-50.focus:outline-none.focus:ring-2.focus:ring-blue-500.focus:border-blue-500.transition-all.duration-200.mb-2
         {:style {:width "200px"}
          :name "twofacode"
          :type "text"
          :required true
          :placeholder "- - - - - -"
          :autoFocus true
          :on (on state digits-count)}]]
       (common/error-message state)
       (button/button-primary-lg
         {:class "w-full mt-3"
          :on {:click (when-let [send (code-submission state)]
                        [send])}}
         "Submit")]]]))
